import React, { useState, useCallback, useEffect } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import { Tag, Tooltip, Input, message, Icon } from 'antd';
import { InputContainer } from '../../../components/Modals/styles';
import { SettingsHeading } from '../../SidebarMenu/styles';
import { updateTags, getMetaTags, addMetaTag, deleteMetaTag } from '../../../utils/services/complexDocumentsAPI';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { MANAGE_TEMPLATE_TAGS_MODAL } from '../../../components/Modals/translationValues';
import { MANAGE_TAGS_AND_FOLDER, META_TAGS } from '../../../components/FileDropdown/translationValues';
import MetaTagsInput from '../../../components/MetaTagsInput';
import MetaTagsList from '../../../components/MetaTagsList';

const NAMESPACES = ['MANAGE_TAGS_AND_FOLDER', 'MANAGE_TEMPLATE_TAGS_MODAL'];

const FileSettings = ({ scopeType, scopeId }) => {
  const { t } = useTranslation(NAMESPACES);

  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TAGS], []);
  const [tags, store] = useDocumentContext(customSelector, ``);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, ``);

  const isOpenDesignSelector = useCallback((store) => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign, updateArtboard] = useArtboardContext(isOpenDesignSelector, `FileSettings`);

  // Use scopeId from props if provided, otherwise fallback to groupId from context
  const groupIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID], []);
  const [groupIdFromContext] = useDocumentContext(groupIdSelector, `FileSettings`);
  const effectiveScopeId = scopeId || groupIdFromContext;
  const effectiveScopeType = scopeType || 'flow-group';

  const [inputValue, setInputValue] = useState('');
  const [tagsList, updateTagList] = useState(tags);
  const [metaTagsList, setMetaTagsList] = useState([]);
  const [loadingMetaTags, setLoadingMetaTags] = useState(false);

  // When the 'Enter' key is pressed, this function is triggered to update the list of tags.
  const handleTagAdd = useCallback(() => {
    // Validation 1: If the input value is empty, do not consider it as a new tag.
    if (inputValue.trim().length === 0) return;
    // Validation 2: Check if the tag already exists.
    const trimmedTag = inputValue.trim();
    const exists = tagsList.some(tag => tag.toLowerCase() === trimmedTag.toLowerCase());
    if (exists) {
      message.error(
        t(
          MANAGE_TEMPLATE_TAGS_MODAL.TAGS_ALREADY_EXIST_ERROR_MESSAGE.key,
          MANAGE_TEMPLATE_TAGS_MODAL.TAGS_ALREADY_EXIST_ERROR_MESSAGE.value,
        ),
      );
    } else {
      const tagAdded = [...tagsList, trimmedTag];
      setInputValue('');
      updateTagList(tagAdded);
      handleSave(tagAdded);
    }
  }, [inputValue, tagsList]);

  // This function is triggered when the close icon is clicked to remove a tag from the list.
  const handleTagRemove = useCallback(
    value => {
      const tagRemoved = tagsList.filter(tag => tag !== value);
      updateTagList(tagRemoved);
      handleSave(tagRemoved);
    },
    [tagsList],
  );

  // This function is triggered when the save button is clicked. The save button will be enabled only if any tags are added or removed.
  const handleSave = useCallback(
    async tags => {
      const response = await updateTags({
        id: store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
        tags: tags.join(','),
        type: currentDesign,
      });
      if (response.statusCode === 1901) {
        message.success(
          t(
            MANAGE_TEMPLATE_TAGS_MODAL.TAGS_UPDATED_SUCCESS_MESSAGE.key,
            MANAGE_TEMPLATE_TAGS_MODAL.TAGS_UPDATED_SUCCESS_MESSAGE.value,
          ),
        );
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.TAGS]: tags });
      } else {
        message.error(
          t(
            MANAGE_TEMPLATE_TAGS_MODAL.FAILED_TO_FETCH_TAGS_ERROR_MESSAGE.key,
            MANAGE_TEMPLATE_TAGS_MODAL.FAILED_TO_FETCH_TAGS_ERROR_MESSAGE.value,
          ),
        );
      }
    },
    [currentDesign, store, t],
  );

  function handleOpenDesign() {
      updateArtboard({
            [CONTEXT_KEYS.SHOW_SIGNUP_PROMPT]: true
      });
  }

  // Load existing meta tags on mount and when scopeId or scopeType changes
  useEffect(() => {
    const loadMetaTags = async () => {
      if (!effectiveScopeId) return;
      
      setLoadingMetaTags(true);
      try {
        const response = await getMetaTags(effectiveScopeType, effectiveScopeId);
        if (response && response.data) {
          // Response is already converted to array format in getMetaTags
          const metaTags = Array.isArray(response.data) 
            ? response.data 
            : [];
          setMetaTagsList(metaTags);
        }
      } catch (error) {
        console.error('Error loading meta tags:', error);
      } finally {
        setLoadingMetaTags(false);
      }
    };

    loadMetaTags();
  }, [effectiveScopeId, effectiveScopeType]);

  // Handle adding a new meta tag
  const handleMetaTagAdd = useCallback(
    async (key, value) => {
      if (!effectiveScopeId) {
        message.error(
          t(
            META_TAGS.FETCH_ERROR.key,
            META_TAGS.FETCH_ERROR.value,
          ),
        );
        return;
      }

      // Generate a temporary ID for the new tag (backend will assign real ID)
      const newMetaTag = {
        id: key.trim(), // Use key as ID since backend uses key for deletion
        key: key.trim(),
        value: value.trim(),
      };

      const updatedMetaTags = [...metaTagsList, newMetaTag];
      setMetaTagsList(updatedMetaTags);

      // Save to backend using POST /{scopeType}/{scopeId} for single key-value
      // Only send the new key-value pair, not the entire list
      try {
        const response = await addMetaTag(effectiveScopeType, effectiveScopeId, key.trim(), value.trim());
        if (response && (response.status === 200 || response.status === 201 || response.statusCode === 1901)) {
          message.success(
            t(
              META_TAGS.ADD_SUCCESS.key,
              META_TAGS.ADD_SUCCESS.value,
            ),
          );
          // No need to reload - the tag is already added to the list
        } else {
          // Revert on error
          setMetaTagsList(metaTagsList);
          message.error(
            t(
              META_TAGS.SAVE_ERROR.key,
              META_TAGS.SAVE_ERROR.value,
            ),
          );
        }
      } catch (error) {
        console.error('Error adding meta tag:', error);
        setMetaTagsList(metaTagsList);
        message.error(
          t(
            META_TAGS.SAVE_ERROR.key,
            META_TAGS.SAVE_ERROR.value,
          ),
        );
      }
    },
    [effectiveScopeId, effectiveScopeType, metaTagsList, t],
  );

  // Handle deleting a meta tag
  const handleMetaTagDelete = useCallback(
    async (metaTagId) => {
      if (!effectiveScopeId) {
        message.error(
          t(
            META_TAGS.FETCH_ERROR.key,
            META_TAGS.FETCH_ERROR.value,
          ),
        );
        return;
      }

      // Find the tag to get its key (used for deletion)
      const tagToDelete = metaTagsList.find(tag => tag.id === metaTagId || tag.key === metaTagId);
      if (!tagToDelete) {
        return;
      }

      const updatedMetaTags = metaTagsList.filter(tag => tag.id !== metaTagId && tag.key !== metaTagId);
      setMetaTagsList(updatedMetaTags);

      try {
        // DELETE /{scopeType}/{scopeId}/{key} - Delete single metadata key
        const response = await deleteMetaTag(effectiveScopeType, effectiveScopeId, tagToDelete.key);
        if (response && (response.status === 200 || response.status === 204)) {
          message.success(
            t(
              META_TAGS.DELETE_SUCCESS.key,
              META_TAGS.DELETE_SUCCESS.value,
            ),
          );
        } else {
          // Revert on error
          setMetaTagsList(metaTagsList);
          message.error(
            t(
              META_TAGS.SAVE_ERROR.key,
              META_TAGS.SAVE_ERROR.value,
            ),
          );
        }
      } catch (error) {
        console.error('Error deleting meta tag:', error);
        setMetaTagsList(metaTagsList);
        message.error(
          t(
            META_TAGS.SAVE_ERROR.key,
            META_TAGS.SAVE_ERROR.value,
          ),
        );
      }
    },
    [effectiveScopeId, effectiveScopeType, metaTagsList, t],
  );

  return (
    <>
      <SettingsHeading docName>
        <div>
          {t(
            MANAGE_TAGS_AND_FOLDER.MANAGE_TAGS_BUTTON.key,
            MANAGE_TAGS_AND_FOLDER.MANAGE_TAGS_BUTTON.value,
          )}
        </div>
      </SettingsHeading>

      <div>
        {tagsList.map((tag, index) => {
          const isLongTag = tag.length > 20;
          const tagElem = (
            <Tag
              className="edit-tag"
              key={tag}
              closable={true}
              onClose={() => handleTagRemove(tag)}
              color="#df4c35"
            >
              <span>{isLongTag ? `${tag.slice(0, 20)}...` : tag}</span>
            </Tag>
          );
          return isLongTag ? (
            <Tooltip title={tag} key={tag}>
              {tagElem}
            </Tooltip>
          ) : (
            tagElem
          );
        })}
        <InputContainer textArea password>
          <Input
            name="tagName"
            type="text"
            className="manageTag-input"
            value={inputValue}
            readOnly={isOpenDesign}
            onClick={isOpenDesign && handleOpenDesign}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={() => handleTagAdd()}
            placeholder={t(
              MANAGE_TEMPLATE_TAGS_MODAL.ADD_TAGS_PLACEHOLDER.key,
              MANAGE_TEMPLATE_TAGS_MODAL.ADD_TAGS_PLACEHOLDER.value,
            )}
            autoComplete={'off'}
          />
        </InputContainer>
      </div>

      <SettingsHeading docName style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {t(
            META_TAGS.TITLE.key,
            META_TAGS.TITLE.value,
          )}
          <Icon type="info-circle" style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.65)' }} />
        </div>
      </SettingsHeading>

      <MetaTagsInput
        groupId={effectiveScopeId}
        scopeType={effectiveScopeType}
        onAdd={handleMetaTagAdd}
        disabled={isOpenDesign}
        t={t}
        existingMetaTags={metaTagsList}
        keyPlaceholder={t(
          META_TAGS.KEY_PLACEHOLDER.key,
          META_TAGS.KEY_PLACEHOLDER.value,
        )}
        valuePlaceholder={t(
          META_TAGS.VALUE_PLACEHOLDER.key,
          META_TAGS.VALUE_PLACEHOLDER.value,
        )}
        addButtonTooltip={t(
          META_TAGS.ADD_META_TAG_TOOLTIP.key,
          META_TAGS.ADD_META_TAG_TOOLTIP.value,
        )}
      />

      <MetaTagsList
        metaTags={metaTagsList}
        onDelete={handleMetaTagDelete}
        disabled={isOpenDesign}
      />
    </>
  );
};

export default withTranslation(NAMESPACES)(FileSettings);
